import { useBox } from "@react-three/cannon";
import { MEZZANINE_Y } from "../../../lib/consts";

const WALL_HEIGHT = MEZZANINE_Y;   // 7
const WALL_T = 0.4;

const LEFT_X  = -4;
const RIGHT_X =  4;
const FRONT_Z = -10;
const BACK_Z  = -20;

// Elevator door opening dimensions — must match Elevator.tsx constants
const ELEVATOR_W      = 2.0;    // elevator width (X span): 2 to 4
const ELEVATOR_D      = 2.0;    // elevator depth (Z span): -14 to -12
const ELEVATOR_DOOR_H = 3.2;    // door panel height
const ELEVATOR_CX     = 3;      // world X centre of elevator (component position)
const ELEVATOR_CZ     = -13;    // world Z centre of elevator

// Elevator front face at x=4 (local: doorFaceX = W/2 = 1.0, world = 3 + 1.0 = 4)
// Hole extents in X (phía trước của thang máy)
const HOLE_WEST  = ELEVATOR_CX - ELEVATOR_W / 2;     // 2 (phía sau)
const HOLE_EAST  = ELEVATOR_CX + ELEVATOR_W / 2;     // 4 (cửa)

// Hole extents in Z
const HOLE_NORTH = ELEVATOR_CZ + ELEVATOR_D / 2;  // -12
const HOLE_SOUTH = ELEVATOR_CZ - ELEVATOR_D / 2;  // -14

function LeftWall() {
	const len = Math.abs(FRONT_Z - BACK_Z);
	const cz  = (FRONT_Z + BACK_Z) / 2;

	const [ref] = useBox(() => ({
		type: "Static" as const,
		args: [WALL_T, WALL_HEIGHT, len],
		position: [LEFT_X, WALL_HEIGHT / 2, cz],
	}));

	return (
		<>
			<mesh ref={ref} visible={false} />
			<mesh position={[LEFT_X, WALL_HEIGHT / 2, cz]} castShadow receiveShadow>
				<boxGeometry args={[WALL_T, WALL_HEIGHT, len]} />
				<meshStandardMaterial color="#ffbfa1" roughness={0.9} />
			</mesh>
		</>
	);
}

function RightWall() {
	// ── The right wall (x=4) has a hole for the elevator door ──
	// Hole in X: from HOLE_WEST (-4) to HOLE_EAST (-2)
	// Hole in Z: from HOLE_NORTH (-12) to HOLE_SOUTH (-14)
	
	// Wall segments:
	//   Front segment (from FRONT_Z to HOLE_NORTH): z from -10 to -12
	const len1 = Math.abs(FRONT_Z - HOLE_NORTH);   // 2.0
	const cz1  = (FRONT_Z + HOLE_NORTH) / 2;       // -11

	//   Back segment (from HOLE_SOUTH to BACK_Z): z from -14 to -20
	const len2 = Math.abs(HOLE_SOUTH - BACK_Z);    // 6.0
	const cz2  = (HOLE_SOUTH + BACK_Z) / 2;        // -17

	// ── Header above the elevator door opening ──
	//   Spans the hole in Z, from ELEVATOR_DOOR_H up to WALL_HEIGHT
	const headerH  = WALL_HEIGHT - ELEVATOR_DOOR_H;            // 3.8
	const headerCY = ELEVATOR_DOOR_H + headerH / 2;            // 5.1
	const holeCZ   = (HOLE_NORTH + HOLE_SOUTH) / 2;            // -13

	const [ref1] = useBox(() => ({
		type: "Static" as const,
		args: [WALL_T, WALL_HEIGHT, len1],
		position: [RIGHT_X, WALL_HEIGHT / 2, cz1],
	}));
	const [ref2] = useBox(() => ({
		type: "Static" as const,
		args: [WALL_T, WALL_HEIGHT, len2],
		position: [RIGHT_X, WALL_HEIGHT / 2, cz2],
	}));
	const [ref3] = useBox(() => ({
		type: "Static" as const,
		args: [WALL_T, headerH, ELEVATOR_D],
		position: [RIGHT_X, headerCY, holeCZ],
	}));

	return (
		<>
			{/* Segment before elevator hole (north side) */}
			<mesh ref={ref1} visible={false} />
			<mesh position={[RIGHT_X, WALL_HEIGHT / 2, cz1]} castShadow receiveShadow>
				<boxGeometry args={[WALL_T, WALL_HEIGHT, len1]} />
				<meshStandardMaterial color="#ffbfa1" roughness={0.9} />
			</mesh>

			{/* Segment after elevator hole (south side) */}
			<mesh ref={ref2} visible={false} />
			<mesh position={[RIGHT_X, WALL_HEIGHT / 2, cz2]} castShadow receiveShadow>
				<boxGeometry args={[WALL_T, WALL_HEIGHT, len2]} />
				<meshStandardMaterial color="#ffbfa1" roughness={0.9} />
			</mesh>

			{/* Header above door opening (closes gap between door top and mezzanine) */}
			<mesh ref={ref3} visible={false} />
			<mesh position={[RIGHT_X, headerCY, holeCZ]} castShadow receiveShadow>
				<boxGeometry args={[WALL_T, headerH, ELEVATOR_D]} />
				<meshStandardMaterial color="#ffbfa1" roughness={0.9} />
			</mesh>
		</>
	);
}

function FrontWall() {
	const spanX = Math.abs(RIGHT_X - LEFT_X) + WALL_T;
	const cx    = (LEFT_X + RIGHT_X) / 2;

	const [ref] = useBox(() => ({
		type: "Static" as const,
		args: [spanX, WALL_HEIGHT, WALL_T],
		position: [cx, WALL_HEIGHT / 2, FRONT_Z],
	}));

	return (
		<>
			<mesh ref={ref} visible={false} />
			<mesh position={[cx, WALL_HEIGHT / 2, FRONT_Z]} castShadow receiveShadow>
				<boxGeometry args={[spanX, WALL_HEIGHT, WALL_T]} />
				<meshStandardMaterial color="#ffbfa1" roughness={0.9} />
			</mesh>
		</>
	);
}

export default function RoomEnclosure() {
	return (
		<>
			<LeftWall />
			<RightWall />
			<FrontWall />
		</>
	);
}
